import React from 'react'

import { Button, Section } from '../../../../components/ui'
import { browserHistory } from '../../../../services/history'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../../lib/booking-entry'

import './location-blurb.css'

// AREA PAGES ONLY, directly under the fold (direction §7, last line: "Area
// pages reuse 7.1 with their own H1 and the LocationBlurb directly under the
// fold"). The slot keeps its name because the running order and the spec pin
// it, but it is no longer one paragraph: it is the whole area section.
//
// WHAT IT IS FOR. A visitor who searched "car wash Battersea" has just read
// the H1 "Mobile car wash in Battersea." in the fold. This section is the
// proof that we work there: what the area is like for the van, how many
// washes we have done in its postcodes, the latest jobs there, the price
// floor and a booking button. So its H2 is "Our work in Battersea", never the
// H1 again, and it carries no price list: the fold directly above already
// prices the three washes, and /services has the rest.
//
// WHY IT IS HERE. app/views/gwmc/_area.html.haml paints this section inside
// #app, and ReactDOM.render clears #app on mount. Area routes fall through to
// HomePage, so until 5 October every visitor with JavaScript lost the local
// wash count, the recent washes, the service mix, the price list and the
// nearby links a second after the page loaded, along with the H2 and every
// H3. Only crawlers ever saw them. Moving the partial outside #app was no
// answer: #app holds the header and footer too, so outside it the section
// lands under the footer.
//
// ONE SOURCE, TWO PAINTS. The server publishes Seo::Area#page_facts twice: as
// the partial's input and as the gwmc:area meta tag read below. Every word and
// every number is formatted on the server, so this file composes sentences
// and nothing more, and it can never state a count the partial did not. The
// markup and classes match the partial element for element, which is what
// keeps the swap on mount invisible (no shift, nothing twice). Change one,
// change the other.
//
// The tag describes the page the visitor landed on. After a client-side route
// change to `/` it is still in <head>, so the section renders only while the
// path is the one the facts were written for. Exported because the running
// order (home-page.jsx) asks the same question: is this an area page?
export const readArea = () => {
  const tag = typeof document === 'undefined' ? null : document.querySelector("meta[name='gwmc:area']")
  try {
    const facts = JSON.parse(tag?.getAttribute('content') || 'null')
    return facts && facts.path === browserHistory.location.pathname ? facts : null
  } catch (e) {
    return null
  }
}

// A photo card or a favourite wash opens the form on that wash (PostHog dead
// clicks, 12 Oct 2026). `book` is the server's path (Seo::Area#book_path_for);
// the SPA push keeps the landing query, utm and gclid included
// (services/history.js). Facts without `book`: the plain form.
const toBooking = (book, source) => event => {
  event.preventDefault()
  enterBooking(new URLSearchParams((book || '').split('?')[1] || '').get('service'), source)
}

const Proof = ({ facts }) => (
  <p className="gwmc-body area-proof">
    {facts.london_total &&
      (facts.wash_count ? (
        <>
          <strong>{facts.wash_count}</strong> cars washed in {facts.postcodes},{' '}
          <strong>{facts.london_total}</strong> across London.{' '}
        </>
      ) : (
        <>
          <strong>{facts.london_total}</strong> cars washed across London.{' '}
        </>
      ))}
    Car washing and valeting by vetted, fully insured professional valeters. The same price wherever
    you are.
  </p>
)

// Image cards only for washes with a scrubbed image, in one row of at most
// three (the server caps it), and every other wash as a line of text. No
// dates (Robin, 15 Oct 2026): a job is its service and outward code.
// The row's modifier is its card count, which is what keeps it from going
// ragged: there is never a second row and never an empty card.
const RecentWashes = ({ photos, washes }) => (
  <div className="area-washes">
    <h3 className="gwmc-h3 area-h3">Washes we’ve done here</h3>
    {photos.length > 0 && (
      <ul className={`area-photos area-photos--${photos.length}`}>
        {photos.map((wash, i) => (
          <li className="area-photo" key={i}>
            {/* With a page: the image and name open the wash's page, "Book
                now" the form on it (Robin, 25 Oct 2026: a label says where it
                goes). Without one (an add-on): the card books, as before. */}
            <a className="area-photo-link" href={wash.page || wash.book || bookingPath(defaultWashSlug())} onClick={wash.page ? undefined : toBooking(wash.book, 'area_photo')}>
              <img
                className="area-photo-img"
                src={wash.photo}
                alt={wash.alt}
                loading="lazy"
                decoding="async"
                width={480}
                height={640}
              />
              <div className="area-photo-text">
                <p className="area-job-service">{wash.service}</p>
                <p className="gwmc-caption">{wash.outcode}</p>
              </div>
            </a>
            {wash.page && (
              <div className="area-photo-go">
                <a className="gwmc-btn gwmc-btn--secondary gwmc-btn--block area-photo-book" href={wash.book || bookingPath(defaultWashSlug())} onClick={toBooking(wash.book, 'area_photo')}>
                  Book now<span className="gwmc-sr-only"> {wash.service}</span>
                </a>
                <a className="gwmc-link area-photo-info" href={wash.page}>
                  See what&apos;s included<span className="gwmc-sr-only"> in the {wash.service}</span>
                </a>
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
    {washes.length > 0 && (
      <ul className="area-jobs">
        {washes.map((wash, i) => (
          <li className="area-job" key={i}>
            <span className="area-job-service">{wash.service}</span>
            <span className="gwmc-caption">{wash.outcode}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
)

export const LocationBlurb = () => {
  const facts = readArea()
  if (!facts) return null

  const {
    name,
    intro,
    see_also = [],
    photos = [],
    recent_washes = [],
    mix = [],
    from_price,
    neighbours = [],
    faqs = []
  } = facts

  return (
    <Section className="area-page" background="stone" aria-labelledby="area-title">
      <div className="area-grid">
        <div className="area-lead">
          <h2 className="gwmc-h2" id="area-title">
            Your local car wash in {name}
          </h2>
          {intro && <p className="gwmc-body area-intro">{intro}</p>}
          <Proof facts={facts} />
          {see_also.map(other => (
            <p className="gwmc-body area-proof" key={other.path}>
              We cover {other.postcodes} from our{' '}
              <a className="gwmc-link" href={other.path}>
                {other.name}
              </a>{' '}
              page.
            </p>
          ))}
        </div>

        {(photos.length > 0 || recent_washes.length > 0) && <RecentWashes photos={photos} washes={recent_washes} />}

        {mix.length > 0 && (
          <div className="area-mix-block">
            <h3 className="gwmc-h3 area-h3">{name}’s favourite washes</h3>
            <ul className="area-mix">
              {mix.map(d => (
                <li className="gwmc-body" key={d.name}>
                  {d.page ? (
                    <>
                      <a className="area-mix-link area-mix-name" href={d.page}>
                        {d.name}
                      </a>
                      {`: ${d.share}% of bookings here, against ${d.london}% across London. `}
                      <a className="gwmc-link area-mix-book" href={d.book || bookingPath(defaultWashSlug())} onClick={toBooking(d.book, 'area_mix')}>
                        Book now<span className="gwmc-sr-only"> {d.name}</span>
                      </a>
                    </>
                  ) : (
                    <a className="area-mix-link" href={d.book || bookingPath(defaultWashSlug())} onClick={toBooking(d.book, 'area_mix')}>
                      <span className="area-mix-name">{d.name}</span>
                      {`: ${d.share}% of bookings here, against ${d.london}% across London.`}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="area-book">
          <p className="gwmc-body area-price-line">
            {from_price ? (
              <>
                Washes from <strong>{from_price}</strong>. Booked in two minutes.
              </>
            ) : (
              'Booked in two minutes.'
            )}
          </p>
          <div className="area-book-actions">
            <Button
              className="area-book-btn"
              variant="primary"
              href={bookingPath(defaultWashSlug())}
              onClick={event => {
                event.preventDefault()
                enterBooking(defaultWashSlug(), 'area_section')
              }}>
              Book your wash in {name}
            </Button>
            <a className="gwmc-link area-book-link" href="/services#compare">
              Compare all washes
            </a>
          </div>
        </div>

        {faqs.length > 0 && (
          <div className="area-faq-block">
            <h3 className="gwmc-h3 area-h3">Questions from {name}</h3>
            <dl className="area-faq">
              {faqs.map(faq => (
                <React.Fragment key={faq.q}>
                  <dt className="area-faq-q">{faq.q}</dt>
                  <dd className="gwmc-body area-faq-a">{faq.a}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}

        {neighbours.length > 0 && (
          <div className="area-nearby-block">
            <h3 className="gwmc-h3 area-h3">Nearby</h3>
            <ul className="area-nearby">
              {neighbours.map(n => (
                <li key={n.path}>
                  <a className="gwmc-link" href={n.path}>
                    {n.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Section>
  )
}
